import React, { useContext, useState, useEffect } from 'react';
import { Box } from 'grommet';
import SHDText from '@ui/Text/SHDText';
import { ScheduleCardProps, ScheduleStatus } from './schedule.d';
import SHDRule from '@ui/Rule/SHDRule';
import GameTracker from './GameTracker/GameTracker';
import { colors } from '@theme/globals/colors';
import { formatScheduleCardDates } from '@helpers/dateUtils';
import { ResponsiveContext } from 'grommet';
import { LiveScoringStatusHookResponse } from './schedule.d';

import type {
  BaseballGameData,
  BasicGameData,
  VolleyballGameData,
} from '@dto/api';
import { COMBINED_BBSB_KEY } from '@constants/strings';

const ScheduleCard: React.FC<ScheduleCardProps> = ({
  scheduleStatus,
  gameInfo,
  onClick,
  viewportSize = null,
  liveScoringStatus,
}) => {
  const scoringData = liveScoringStatus as LiveScoringStatusHookResponse;
  const [home, setHome] = useState<string | undefined>(undefined);
  const [away, setAway] = useState<string | undefined>(undefined);
  const [homeScore, setHomeScore] = useState<number>(0);
  const [awayScore, setAwayScore] = useState<number>(0);
  const [scheduleDate, setScheduleDate] = useState<string | undefined>(
    undefined
  );
  const [scheduleTime, setScheduleTime] = useState<string | undefined>(
    undefined
  );

  const [weAreHomeTeam, setWeAreHomeTeam] = useState<boolean | number>(
    gameInfo?.scoreWeAreHome || false
  );

  const [runners, setRunners] = useState<number[]>([0, 0, 0, 0]);
  const [inning, setInning] = useState<{ half: string; inning: number }>({
    half: 'top',
    inning: 1,
  });
  const [outs, setOuts] = useState<number>(0);

  const isFinal =
    gameInfo?.isEnded || scoringData?.gameStatus?.isEnded || false;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isLive = scheduleStatus === ScheduleStatus.LIVE;
  const isBBSB = gameInfo?.sportType === COMBINED_BBSB_KEY;
  const viewportSizeContext = useContext(ResponsiveContext);

  viewportSize = viewportSize || viewportSizeContext;

  // Set the game date an time
  useEffect(() => {
    const { date, time } = formatScheduleCardDates(
      gameInfo?.scheduleTsStart || 0,
      gameInfo?.localTzString || 'UTC'
    );
    setScheduleDate(date);
    setScheduleTime(time);
  }, [gameInfo]);

  useEffect(() => {
    const isHomeTeam = gameInfo?.scoreWeAreHome === 1;
    setWeAreHomeTeam(isHomeTeam);
    if (scoringData?.gameStatus) {
      setWeAreHomeTeam(scoringData?.gameStatus.weAreHome);
    }
  }, [gameInfo, scoringData?.gameStatus]);

  useEffect(() => {
    let gameAwayTeam = gameInfo?.scoreOurName;
    let gameHomeTeam = gameInfo?.scoreOpponentName;
    if (weAreHomeTeam) {
      gameAwayTeam = gameInfo?.scoreOpponentName;
      gameHomeTeam = gameInfo?.scoreOurName;
    }
    setHome(gameHomeTeam);
    setAway(gameAwayTeam);
  }, [gameInfo, weAreHomeTeam]);

  type LiveSportStatus = BaseballGameData | VolleyballGameData | BasicGameData;

  useEffect(() => {
    let homeGameScore = 0;
    let awayGameScore = 0;
    const getSportScores = (
      isHomeTeam: boolean | number,
      liveSportStatus: LiveSportStatus
    ) => {
      const { ourScore, opponentScore } = liveSportStatus;
      const normalizeOurScore = ourScore === -1 ? 0 : ourScore;
      const normalizeOpponentScore = opponentScore === -1 ? 0 : opponentScore;

      return isHomeTeam
        ? { home: normalizeOurScore, away: normalizeOpponentScore }
        : { home: normalizeOpponentScore, away: normalizeOurScore };
    };
    const liveSport =
      scoringData?.baseball ||
      gameInfo?.baseball ||
      scoringData?.volleyball ||
      gameInfo?.volleyball ||
      scoringData?.otherSport ||
      gameInfo?.otherSport;

    if (liveSport) {
      const { home: hs, away: as } = getSportScores(weAreHomeTeam, liveSport);
      homeGameScore = hs || 0;
      awayGameScore = as || 0;
    }
    setHomeScore(homeGameScore);
    setAwayScore(awayGameScore);
  }, [
    gameInfo,
    scoringData?.gameStatus,
    scoringData?.baseball,
    scoringData?.volleyball,
    scoringData?.otherSport,
    weAreHomeTeam,
  ]);

  // Live Baseball Updates
  useEffect(() => {
    if (isBBSB && scoringData?.baseball) {
      const halfInning = scoringData?.baseball.inningTop ? 'top' : 'bottom';
      setInning({
        half: halfInning,
        inning: scoringData?.baseball.inning || 1,
      });
      const baseRunners =
        scoringData?.baseball.baseRunners?.map(
          (runner: string, index: number) => (runner !== '' ? index : 0)
        ) || [];
      setRunners(baseRunners);
      setOuts(scoringData?.baseball.outs || 0);
    }
  }, [scoringData?.baseball, isBBSB]);

  return (
    <Box
      data-testid={`schedule-card`}
      width={
        viewportSize === 'small' || viewportSize === 'medium' ? '100%' : '49%'
      }
      className="schedule-card"
      background="white"
      pad="small"
      round={{ size: 'small' }}
      margin={{ vertical: 'xxsmall' }}
      direction="row"
      // {...(isLive && {
      //   margin: { top: '0px', bottom: 'xxsmall' },
      //   round: { corner: 'bottom' },
      // })}
      onClick={() => onClick && onClick(scheduleStatus, gameInfo)}
    >
      <Box width="100%">
        <Box
          direction="row"
          justify="between"
          align="center"
          margin={{ vertical: 'xxsmall' }}
        >
          <SHDText style={'subhead'}>{away}</SHDText>
          {(gameInfo?.scoreTsStart !== 0.0 || scoringData?.gameStatus) && (
            <SHDText dataTestId="team-away-score" style={'score'}>
              {awayScore}
            </SHDText>
          )}
        </Box>
        <Box
          direction="row"
          justify="between"
          align="center"
          margin={{ vertical: 'xxsmall' }}
        >
          <SHDText style={'subhead'}>{home}</SHDText>
          {(gameInfo?.scoreTsStart !== 0.0 || scoringData?.gameStatus) && (
            <SHDText dataTestId="team-home-score" style={'score'}>
              {homeScore}
            </SHDText>
          )}
        </Box>
      </Box>

      <Box margin={{ horizontal: '15px' }}>
        <SHDRule
          color="black-alpha-10"
          thickness="1px"
          length="65px"
          direction="column"
        />
      </Box>
      <Box width={{ min: '60px' }}>
        {
          <>
            {scoringData && !scoringData?.gameStatus && (
              <ScheduleDateAndTime
                date={scheduleDate}
                time={scheduleTime}
                final={isFinal}
              />
            )}
            {scoringData?.baseball && (
              <GameTracker inning={inning} outs={outs} runners={runners} />
            )}
            {(scoringData?.volleyball || scoringData?.otherSport) && (
              <OtherSportsLive />
            )}
          </>
          //)
        }
      </Box>
    </Box>
  );
};

export default ScheduleCard;

const OtherSportsLive = () => {
  return (
    <Box
      data-testid="other-sports-live"
      className="other-sports-live"
      direction="column"
      justify="center"
      align="center"
      margin={'auto'}
    >
      <SHDText
        dataTestId="live"
        style={'footnote'}
        color={colors['black-alpha-50'] as string}
      >
        Live
      </SHDText>
    </Box>
  );
};
const ScheduleDateAndTime: React.FC<{
  date: string | undefined;
  time: string | undefined;
  final: boolean;
}> = ({ date, time, final = false }) => {
  const txtColor = colors['black-alpha-50'] as string;
  return (
    <Box
      data-testid="schedule-date-time"
      className="schedule-date-time"
      direction="column"
      justify="center"
      align="center"
      margin={'auto'}
    >
      <SHDText dataTestId="schedule-date" style={'footnote'} color={txtColor}>
        {date}
      </SHDText>
      <SHDText dataTestId="schedule-time" style={'footnote'} color={txtColor}>
        {final ? 'Final' : time}
      </SHDText>
    </Box>
  );
};
